import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, Sparkles, Loader2, RefreshCw } from "lucide-react";
import {
  AdminGate, StatusPill, BUSY_STATUSES, fmtDay, inputCls, FieldLabel, minPublishDay, MIN_REVIEW_DAYS,
} from "@/components/admin/InsightShared";

export default function InsightsComposer() {
  return (
    <AdminGate returnTo="/admin/insights">
      <ComposerPage />
    </AdminGate>
  );
}

function ComposerPage() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/insight-drafts");
      setDrafts(data.drafts || []);
    } catch {
      toast.error("Could not load drafts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!drafts.some((d) => BUSY_STATUSES.includes(d.status))) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [drafts]);

  return (
    <div className="bg-white min-h-screen" data-testid="admin-insights-page">
      <Helmet>
        <title>Insight Composer · SideB Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.16em] text-foreground/55 hover:text-[#ac62ea] mb-8" data-testid="admin-insights-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Admin
        </Link>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="label-mono">Admin · Content</div>
            <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-[-0.03em] mt-3">Insight Composer</h1>
            <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">
              Give the AI writer a topic or a source URL. It drafts the post, emails swiles@sideb.io to review,
              revises on your notes, and publishes on the chosen date once you approve. Every draft gets at least a 5-day review window.
            </p>
          </div>
          <Button
            className="rounded-full bg-[#ac62ea] hover:bg-[#9851d6] text-white h-10 px-5"
            onClick={() => setOpen(true)}
            data-testid="admin-insights-new-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New AI draft
          </Button>
        </div>
        <DraftsTable drafts={drafts} loading={loading} onRefresh={load} />
      </div>
      <NewDraftDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function DraftsTable({ drafts, loading, onRefresh }) {
  return (
    <div className="mt-10 rounded-2xl border border-border overflow-hidden" data-testid="admin-insights-table">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/30">
        <span className="text-xs font-mono uppercase tracking-[0.14em] text-foreground/60">{drafts.length} drafts</span>
        <button onClick={onRefresh} className="text-foreground/50 hover:text-[#ac62ea] transition-colors" data-testid="admin-insights-refresh">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      {loading ? (
        <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
      ) : drafts.length === 0 ? (
        <div className="p-10 text-center text-sm text-muted-foreground" data-testid="admin-insights-empty">
          No drafts yet. Start one with “New AI draft”.
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {drafts.map((d) => (
            <li key={d.id}>
              <Link
                to={`/admin/insights/${d.id}`}
                className="flex items-center gap-4 px-5 py-4 hover:bg-muted/30 transition-colors"
                data-testid={`admin-insights-row-${d.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{d.title || d.topic}</div>
                  <div className="text-xs text-muted-foreground mt-1 truncate">
                    {d.category} · Publish {fmtDay(d.publish_at)} · by {d.created_by}
                  </div>
                </div>
                {BUSY_STATUSES.includes(d.status) && <Loader2 className="h-4 w-4 animate-spin text-sky-600" />}
                <StatusPill status={d.status} testId={`admin-insights-status-${d.id}`} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const emptyForm = () => ({ topic: "", source_url: "", guidance: "", publish_at: minPublishDay() });

function NewDraftDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (form.topic.trim().length < 3) {
      toast.error("Add a topic for the AI writer.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/insight-drafts", {
        topic: form.topic.trim(),
        source_url: form.source_url.trim() || null,
        guidance: form.guidance.trim() || null,
        publish_at: form.publish_at || null,
      });
      toast.success("AI is drafting. You'll get an email when it's ready for review.");
      setForm(emptyForm());
      onOpenChange(false);
      navigate(`/admin/insights/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the draft");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="admin-insights-new-dialog">
        <DialogHeader>
          <DialogTitle>New AI draft</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 mt-2">
          <label className="grid gap-1.5">
            <FieldLabel>Topic *</FieldLabel>
            <input className={inputCls} value={form.topic} onChange={update("topic")} placeholder="e.g. How AI is changing RevOps conversations" data-testid="admin-insights-new-topic" />
          </label>
          <label className="grid gap-1.5">
            <FieldLabel>Source URL (optional)</FieldLabel>
            <input className={inputCls} type="url" value={form.source_url} onChange={update("source_url")} placeholder="https://…" data-testid="admin-insights-new-url" />
          </label>
          <label className="grid gap-1.5">
            <FieldLabel>Angle / guidance (optional)</FieldLabel>
            <textarea className={inputCls} rows={4} value={form.guidance} onChange={update("guidance")} placeholder="Audience, points to hit, tone, things to avoid…" data-testid="admin-insights-new-guidance" />
          </label>
          <label className="grid gap-1.5">
            <FieldLabel>Publish date (min {MIN_REVIEW_DAYS} days out for review)</FieldLabel>
            <input className={inputCls} type="date" min={minPublishDay()} required value={form.publish_at} onChange={update("publish_at")} data-testid="admin-insights-new-date" />
          </label>
        </div>
        <DialogFooter className="mt-4 gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="admin-insights-new-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#ac62ea] hover:bg-[#9851d6] text-white" data-testid="admin-insights-new-submit">
            {submitting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
            Generate draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
